/**
 * Dipendenti sul telefono (7/10/2026): risponde a "chi è in busta paga, è
 * pagato, e cosa manca?".
 *
 * Prima, a 390px: una frase di cinque righe, tre riquadri di numeri, una nota,
 * poi schede alte uno schermo con sei etichette maiuscole per persona, e la
 * barra "Nuova persona in busta paga" sempre in fondo.
 * Qui la forma è una LISTA DI PERSONE:
 *   1. in grande, quanto esce ogni mese per il personale (stima dalla banca);
 *      sotto, se c'è qualcosa da verificare, in rosso;
 *   2. le persone, una riga ciascuna: iniziali, nome, fino a quando è pagata
 *      (o cosa manca, in rosso), il netto al mese. Si tocca e sale la sua
 *      scheda: rapporto, medie, gli ultimi bonifici, modifica, termina;
 *   3. i versamenti da verificare (TFR e stipendi): si tocca e si decide,
 *      pagato altrove (con la nota) o da recuperare;
 *   4. il mese tipo: quando partono stipendi, TFR, F24.
 * "Nuova persona in busta paga" si usa di rado: sta nel menu ⋯ in alto.
 * Stessa chiamata e stessa cache del computer (/admin/dipendenti).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EMPLOYEE_KIND_LABEL, PAYROLL_KIND_LABEL } from '@masamune/shared';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { MCampo, MEmpty, MGroup, MHero, MMore, MPage, MRow, iniziali } from '../../../components/mobile/MobileKit';
import { fmtEur } from '../../../lib/utils';
import { adminHref, canSeeAdminPage } from '../../../lib/adminRoutes';
import { daysFromToday, plurale } from '../adminApi';
import { AdminHeader } from '../shared/AdminShell';
import { Fatto, Foglio, Gesto, Testo, euro, euroRiga, giornoBreve } from '../shared/AdminMobile';
import { apriSchedaPersona } from '../../../components/modals/NewEmployeeModal';
import { dipendentiQuery, useDipendentiActions, type Chiusa, type DipendentiData, type Persona, type Verifica } from './dipendentiApi';

// ── Parole (le stesse della pagina del computer) ────────────────────

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const nomeMese = (ym: string) => MESI[Number(ym.slice(5, 7)) - 1] ?? ym;
const meseAnno = (ym: string) => `${nomeMese(ym)} ${ym.slice(0, 4)}`;
const aMese = (ym: string) => (nomeMese(ym).startsWith('a') ? 'ad' : 'a');
const meseCorto = (ym: string, anno: string) => (ym.slice(0, 4) === anno ? nomeMese(ym) : meseAnno(ym));

function periodo(from: string, to: string): string {
  if (from === to) return meseAnno(from);
  if (from.slice(0, 4) === to.slice(0, 4)) return `da ${nomeMese(from)} ${aMese(to)} ${meseAnno(to)}`;
  return `da ${meseAnno(from)} ${aMese(to)} ${meseAnno(to)}`;
}

function elenco(xs: string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} e ${xs.at(-1)}`;
}

function tempoFa(iso: string | null | undefined): string {
  const n = daysFromToday(iso);
  if (n == null) return '';
  const g = -n;
  if (g <= 0) return g === 0 ? 'oggi' : 'tra poco';
  if (g === 1) return 'ieri';
  if (g < 14) return `${g} giorni fa`;
  if (g < 60) return `${Math.round(g / 7)} settimane fa`;
  if (g < 548) return `${Math.round(g / 30.4)} mesi fa`;
  const anni = Math.round(g / 365);
  return anni === 1 ? '1 anno fa' : `${anni} anni fa`;
}

function daQuanto(iso: string | null | undefined): string {
  const n = daysFromToday(iso);
  if (n == null || n > 0) return '';
  const g = -n;
  if (g < 45) return `da ${g} giorni`;
  if (g < 365) return `da ${Math.max(2, Math.round(g / 30.4))} mesi`;
  const anni = Math.round(g / 365);
  return anni === 1 ? 'da 1 anno' : `da ${anni} anni`;
}

const primo = (p: Persona) => p.fullName.split(' ')[0] ?? p.fullName;

/** Il TFR pagato in blocco cita più mesi in causale: "fino a dicembre 2023". */
const RE_PIU_MESI = /\b(gen|feb|mar|apr|mag|giu|lug|ago|set|ott|nov|dic)[a-z]*\b[^]*\b(gen|feb|mar|apr|mag|giu|lug|ago|set|ott|nov|dic)[a-z]*\b/i;

/** Lo stato di stipendi e TFR di una persona, detto in poche parole (rosso solo se c'è da fare). */
function statoPersona(p: Persona, anno: string): { testo: string; alert: boolean } {
  if (!p.active) return { testo: p.endedAt ? `rapporto finito ${tempoFa(p.endedAt)}` : 'rapporto finito', alert: false };
  const { tfr, stipendio, daRecuperare } = p.verificheAperte;
  const tot = tfr + stipendio;
  if (tot) {
    const cosa = stipendio && tfr ? 'stipendi e TFR' : stipendio ? 'stipendio' : 'TFR';
    return { testo: `${cosa} ${daRecuperare === tot ? 'da recuperare' : 'da verificare'}`, alert: true };
  }
  const u = p.ultimoStipendio?.month;
  return { testo: u ? `pagato fino ${aMese(u)} ${meseCorto(u, anno)}` : 'nessuno stipendio in banca', alert: false };
}

/** Quale pannello è aperto: una persona, un versamento da verificare, uno già chiuso. */
type Pannello = null | { kind: 'persona'; p: Persona } | { kind: 'verifica'; v: Verifica } | { kind: 'chiusa'; c: Chiusa };

export function DipendentiMobile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const allowed = canSeeAdminPage(user, 'dipendenti');
  const q = useQuery({ ...dipendentiQuery, enabled: allowed });
  const d = q.data;
  const [foglio, setFoglio] = useState<Pannello>(null);
  const [altrove, setAltrove] = useState<Verifica | null>(null);
  const [nota, setNota] = useState('');
  const [chiuseAperte, setChiuseAperte] = useState(false);
  const { decidi } = useDipendentiActions();

  if (!allowed) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Dipendenti" />
        <MPage><MEmpty>Stipendi e compensi li vede solo chi ha il livello Admin sull'Amministrazione.</MEmpty></MPage>
      </div>
    );
  }

  const header = (
    <AdminHeader
      title="Dipendenti"
      menu={[{ key: 'nuova', label: 'Nuova persona in busta paga', icon: 'plus', onClick: () => apriSchedaPersona() }]}
    />
  );
  if (q.isError) return <div className="page adm-page">{header}<MPage><MEmpty>I dati dei dipendenti non sono arrivati: riprova tra qualche secondo.</MEmpty></MPage></div>;
  if (!d) return <div className="page adm-page">{header}<MPage><MEmpty>Carico le persone in busta paga…</MEmpty></MPage></div>;

  const anno = d.today.slice(0, 4);
  const attive = d.persone.filter((p) => p.active);
  const finite = d.persone.filter((p) => !p.active);
  const nVerifiche = d.verifiche.length;

  // Nella riga: fino a quando è pagata. Cosa manca lo dice il gruppo "Versamenti
  // da verificare" qui sotto: ripeterlo in rosso su ogni persona era un doppione,
  // e una lista tutta rossa.
  const rigaPersona = (p: Persona) => {
    const u = p.ultimoStipendio?.month;
    const sub = !p.active
      ? statoPersona(p, anno).testo
      : u ? `pagato fino ${aMese(u)} ${meseCorto(u, anno)}` : 'nessuno stipendio in banca';
    return (
      <MRow
        key={p.id}
        text={iniziali(p.fullName)}
        round
        title={p.fullName}
        // Il mese è l'informazione utile: se non ci sta, va a capo invece di tagliarsi.
        sub={sub}
        subWrap
        trail={p.netto.media != null ? euro(p.netto.media) : undefined}
        trailSub={p.netto.media != null ? (p.netto.insieme ? 'netto e km' : 'netto al mese') : undefined}
        onClick={() => setFoglio({ kind: 'persona', p })}
      />
    );
  };

  return (
    <div className="page adm-page">
      {header}
      <MPage>
        <MHero
          label="Per il personale, ogni mese"
          unit="€"
          value={fmtEur(d.fascia.uscitaMensile)}
          sub={<>stima dai pagamenti in banca{nVerifiche > 0
            ? <> · <span className="is-alert">{plurale(nVerifiche, 'versamento da verificare', 'versamenti da verificare')}</span></>
            : ' · stipendi e TFR in regola'}</>}
        />

        <MGroup title="In busta paga" count={attive.length}
          note="Sono i pagamenti visti in banca, non il costo aziendale: quello lo dà il consulente del lavoro.">
          {attive.length === 0
            ? <MEmpty>Nessuna persona in busta paga: si aggiunge dal menu ⋯ in alto.</MEmpty>
            : attive.map(rigaPersona)}
        </MGroup>

        {nVerifiche > 0 && (
          <MGroup title="Versamenti da verificare" count={nVerifiche}>
            {d.verifiche.map((v) => {
              const n = v.months.length;
              const p = d.persone.find((x) => x.id === v.employeeId);
              const unMeseDiTfr = v.kind === 'TFR' && n === 1 && p?.tfrAlMese != null;
              return (
                <MRow
                  key={v.key}
                  icon={v.kind === 'TFR' ? 'coins' : 'banknote'}
                  tone={v.stato === 'DA_RECUPERARE' ? 'alert' : undefined}
                  title={`${v.kind === 'TFR' ? 'TFR' : 'Stipendio'} di ${p ? primo(p) : v.fullName}`}
                  sub={`${periodo(v.from, v.to)}${n > 1 ? `, ${plurale(n, 'mese', 'mesi')}` : ''}${v.stato === 'DA_RECUPERARE' ? ' · da recuperare' : ''}`}
                  subWrap
                  trail={unMeseDiTfr ? undefined : v.stima != null ? `~${euro(v.stima)}` : undefined}
                  onClick={() => setFoglio({ kind: 'verifica', v })}
                />
              );
            })}
          </MGroup>
        )}

        <MeseTipo d={d} onVai={(to) => navigate(to)} />

        {(finite.length > 0 || d.chiuse.length > 0) && (
          <MGroup title="Archivio">
            {finite.map(rigaPersona)}
            {d.chiuse.length > 0 && !chiuseAperte && (
              <MRow icon="check" title="Versamenti chiusi perché pagati altrove" sub={plurale(d.chiuse.length, 'voce, con la nota', 'voci, con la nota')} onClick={() => setChiuseAperte(true)} chevron={false} />
            )}
            {chiuseAperte && d.chiuse.map((c) => (
              <MRow key={c.key} icon="check" title={`${c.kind === 'TFR' ? 'TFR' : 'Stipendio'} di ${c.fullName}`} sub={c.nota ?? periodo(c.from, c.to)} onClick={() => setFoglio({ kind: 'chiusa', c })} />
            ))}
          </MGroup>
        )}
      </MPage>

      {foglio?.kind === 'persona' && <FoglioPersona p={foglio.p} anno={anno} onClose={() => setFoglio(null)} />}
      {foglio?.kind === 'verifica' && (
        <FoglioVerifica v={foglio.v} d={d} anno={anno} onClose={() => setFoglio(null)}
          onAltrove={() => { const v = foglio.v; setFoglio(null); setNota(''); setAltrove(v); }} />
      )}
      {foglio?.kind === 'chiusa' && (
        <Foglio open onClose={() => setFoglio(null)} title={`${foglio.c.kind === 'TFR' ? 'TFR' : 'Stipendio'} di ${foglio.c.fullName}`}
          primary={(
            <button className="btn m-block" disabled={decidi.isPending} onClick={() => {
              const c = foglio.c;
              setFoglio(null);
              decidi.mutate({ employeeId: c.employeeId, kind: c.kind, months: c.months, resolution: 'RIAPERTA' });
            }}>
              <Icon name="undo" size={12} /> Rimetti da verificare
            </button>
          )}>
          <div className="mk-list">
            <Fatto label="Periodo" value={periodo(foglio.c.from, foglio.c.to)} />
            <Fatto label="Chiuso" value={<>{foglio.c.decisoIl ? tempoFa(foglio.c.decisoIl) : '—'}{foglio.c.decisoDa && <span className="s">da {foglio.c.decisoDa}</span>}</>} />
          </div>
          <div className="mk-list"><Testo>{foglio.c.nota ?? 'Senza nota.'}</Testo></div>
        </Foglio>
      )}

      {/* "Segna pagato altrove": una nota obbligatoria, perché fra sei mesi qualcuno chiederà dove. */}
      <MCampo
        open={!!altrove}
        onClose={() => setAltrove(null)}
        titolo="Come o dove è stato pagato"
        nota={altrove ? `${altrove.kind === 'TFR' ? 'TFR' : 'Stipendio'} di ${altrove.fullName}, ${periodo(altrove.from, altrove.to)}. La voce esce da quelle da verificare e resta in archivio con la tua nota.` : undefined}
        campo={<input className="inp" autoFocus placeholder="Es. versato dal consulente, pagato in contanti" value={nota} onChange={(e) => setNota(e.target.value)} />}
        onSalva={() => {
          if (!altrove || nota.trim().length < 3 || decidi.isPending) return;
          const v = altrove;
          decidi.mutate({ employeeId: v.employeeId, kind: v.kind, months: v.months, resolution: 'ALTROVE', note: nota.trim() }, { onSuccess: () => setAltrove(null) });
        }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Il mese tipo
// ════════════════════════════════════════════════════════════════════

function MeseTipo({ d, onVai }: { d: DipendentiData; onVai: (to: string) => void }) {
  const nomi = new Map(d.persone.map((p) => [p.fullName, primo(p)]));
  const breve = (n: string) => nomi.get(n) ?? n;
  const righe: Array<{ icon: IconName; cosa: string; quando: string; to?: string }> = [];
  const fissi = new Map<number, string[]>();
  for (const s of d.meseTipo.stipendi) if (s.fisso) fissi.set(s.giorno, [...(fissi.get(s.giorno) ?? []), breve(s.nome)]);
  for (const [g, chi] of fissi) righe.push({ icon: 'banknote', cosa: `${chi.length > 1 ? 'Stipendi' : 'Stipendio'} di ${elenco(chi)}`, quando: `intorno al ${g} del mese dopo` });
  for (const s of d.meseTipo.stipendi.filter((x) => !x.fisso)) {
    righe.push({ icon: 'banknote', cosa: `Stipendio di ${breve(s.nome)}`, quando: s.stimato ? 'nessun saldo in banca da cui capire il giorno' : 'in più bonifici, senza un giorno fisso' });
  }
  const conTfr = d.persone.filter((p) => p.active && p.tfrAlMese != null).map(primo);
  if (conTfr.length) righe.push({ icon: 'coins', cosa: `TFR di ${elenco(conTfr)}`, quando: `ad Alleanza, intorno al ${d.meseTipo.tfrGiorno ?? 10} del mese dopo` });
  righe.push({ icon: 'percent', cosa: 'F24 delle trattenute in busta', quando: `entro il ${d.meseTipo.f24Giorno} del mese dopo`, to: adminHref('fisco', { vista: 'f24' }) });
  if (d.meseTipo.jetHrGiorno) righe.push({ icon: 'file', cosa: 'Buste paga di Jet HR', quando: `addebito intorno al ${d.meseTipo.jetHrGiorno} del mese`, to: adminHref('spese', { vista: 'fisse' }) });
  return (
    <MGroup title="Il mese tipo">
      {righe.map((r) => (
        <MRow key={r.cosa} icon={r.icon} title={r.cosa} sub={r.quando} onClick={r.to ? () => onVai(r.to!) : undefined} />
      ))}
    </MGroup>
  );
}

// ════════════════════════════════════════════════════════════════════
// I pannelli
// ════════════════════════════════════════════════════════════════════

function FoglioPersona({ p, anno, onClose }: { p: Persona; anno: string; onClose: () => void }) {
  const { riprendi } = useDipendentiActions();
  const [tutti, setTutti] = useState(false);
  const st = statoPersona(p, anno);
  const u = p.ultimoStipendio;
  const pagamenti = [...p.pagamenti].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  const primoStipendio = p.pagamenti.filter((x) => x.kind === 'STIPENDIO' && x.month).map((x) => x.month!).sort()[0];
  return (
    <Foglio open onClose={onClose} title={p.fullName}
      primary={<button className="btn m-block" onClick={() => { onClose(); apriSchedaPersona(p, 'modifica'); }}><Icon name="pencil" size={12} /> Modifica la scheda</button>}>
      <div className="mk-list">
        <Fatto label="Rapporto" value={<>{EMPLOYEE_KIND_LABEL[p.kind]}{p.partTimePct != null && p.partTimePct < 100 && <span className="s">part time {p.partTimePct}%</span>}</>} />
        <Fatto label="In azienda" value={p.hiredAt ? daQuanto(p.hiredAt) || giornoBreve(p.hiredAt) : primoStipendio ? `primo stipendio ${meseAnno(primoStipendio)}` : '—'} />
        <Fatto label={p.netto.insieme ? 'Netto e rimborsi km' : 'Netto al mese'}
          value={<>{p.netto.media != null ? euro(p.netto.media) : '—'}{p.netto.mesi.length > 0 && <span className="s">media di {plurale(p.netto.mesi.length, 'mese', 'mesi')}</span>}</>} />
        {p.tfrAlMese != null && <Fatto label="TFR al mese" value={euro(p.tfrAlMese)} />}
        <Fatto label="Ultimo stipendio" value={<>{u?.month ? meseCorto(u.month, anno) : 'nessuno'}{u?.paidOn && <span className="s">pagato {tempoFa(u.paidOn)}</span>}</>} />
        <Fatto label="Stipendi e TFR" alert={st.alert} value={st.testo} />
      </div>

      {pagamenti.length > 0 && (
        <MGroup title="Ultimi bonifici" count={pagamenti.length}>
          {(tutti ? pagamenti : pagamenti.slice(0, 5)).map((x) => (
            <MRow
              key={x.id}
              title={`${PAYROLL_KIND_LABEL[x.kind]}${x.month && x.kind !== 'ALTRO'
                ? x.kind === 'TFR' && RE_PIU_MESI.test(x.causale) ? ` fino a ${meseCorto(x.month, anno)}` : ` di ${meseCorto(x.month, anno)}`
                : ''}`}
              sub={`${giornoBreve(x.date)} · ${tempoFa(x.date)}`}
              trail={euroRiga(x.amount)}
            />
          ))}
          {pagamenti.length > 5 && <MMore label={tutti ? 'Mostra meno' : `Vedi tutti i ${pagamenti.length}`} onClick={() => setTutti((v) => !v)} />}
        </MGroup>
      )}

      <div className="mk-list">
        {p.active
          ? <Gesto icon="logout" label="Termina il rapporto" sub="le spese fisse si fermano dopo l'ultima mensilità" danger onClick={() => { onClose(); apriSchedaPersona(p, 'termina'); }} />
          : <Gesto icon="undo" label="Il rapporto è ancora in corso" disabled={riprendi.isPending} onClick={() => { onClose(); riprendi.mutate({ id: p.id, nome: p.fullName }); }} />}
      </div>
    </Foglio>
  );
}

function FoglioVerifica({ v, d, anno, onClose, onAltrove }: { v: Verifica; d: DipendentiData; anno: string; onClose: () => void; onAltrove: () => void }) {
  const { decidi } = useDipendentiActions();
  const p = d.persone.find((x) => x.id === v.employeeId);
  const n = v.months.length;
  const acconti = v.acconti.reduce((s, a) => s + a.amount, 0);
  // Cosa dice la banca: le stesse frasi della pagina del computer.
  let banca: string;
  if (v.kind === 'TFR') {
    banca = n > 1
      ? `Nessun bonifico ad Alleanza con la sua polizza${p?.tfrRef ? ` (n. ${p.tfrRef})` : ''} per questi mesi.`
      : `Il TFR di solito parte intorno al ${Number(v.dovevaPartire.slice(8, 10))} del mese dopo: questo doveva partire ${tempoFa(v.dovevaPartire)} e in banca non c'è.`;
  } else {
    const ult = p?.ultimoStipendio?.month;
    const soloAcconto = v.acconti.map((a) => nomeMese(a.month));
    banca = `${ult ? `L'ultimo mese pagato per intero è ${meseCorto(ult, anno)}.` : 'In banca non c\'è nessuno stipendio pagato per intero.'}${soloAcconto.length ? ` Di ${elenco(soloAcconto)} è arrivato solo un acconto.` : ''}`;
  }
  const stima = v.stima == null ? null : v.kind === 'TFR'
    ? `${plurale(n, 'mese', 'mesi')} per il TFR al mese di oggi: la cifra vera la dà il consulente del lavoro.`
    : `${plurale(n, 'mese', 'mesi')} per la media mensile${acconti ? `, meno ${euro(acconti)} di acconti già pagati` : ''}.`;
  return (
    <Foglio open onClose={onClose} title={`${v.kind === 'TFR' ? 'TFR' : 'Stipendio'} di ${v.fullName}`}
      primary={<button className="btn btn-primary m-block" disabled={decidi.isPending} onClick={onAltrove}><Icon name="check" size={12} /> Segna pagato altrove</button>}>
      <div className="mk-list">
        <Fatto label="Periodo" value={<>{periodo(v.from, v.to)}{n > 1 && <span className="s">{plurale(n, 'mese', 'mesi')}</span>}</>} />
        <Fatto label="Importo stimato" value={v.stima != null ? `circa ${euro(v.stima)}` : '—'} />
        <Fatto label="Stato" alert={v.stato === 'DA_RECUPERARE'} value={<>{v.stato === 'DA_RECUPERARE' ? 'Da recuperare' : 'Da verificare'}{v.decisoIl && <span className="s">{v.decisoDa ? `${v.decisoDa}, ` : ''}{tempoFa(v.decisoIl)}</span>}</>} />
      </div>
      <div className="mk-list">
        <Testo>{banca}</Testo>
        {stima && <Testo>{stima}</Testo>}
      </div>
      <div className="mk-list">
        {v.stato === 'DA_VERIFICARE' ? (
          <Gesto icon="alert" label="Segna da recuperare" sub="il versamento manca davvero: resta qui finché non arriva il bonifico" disabled={decidi.isPending}
            onClick={() => { onClose(); decidi.mutate({ employeeId: v.employeeId, kind: v.kind, months: v.months, resolution: 'DA_RECUPERARE' }); }} />
        ) : (
          <Gesto icon="undo" label="Rimetti da verificare" disabled={decidi.isPending}
            onClick={() => { onClose(); decidi.mutate({ employeeId: v.employeeId, kind: v.kind, months: v.months, resolution: 'RIAPERTA' }); }} />
        )}
      </div>
    </Foglio>
  );
}
